"use client";

/**
 * The follow-up on a feedback ticket, in the app (owner, 2026-10-16:
 * "most of them do not check the email... add follow-ups into the
 * frontend: our reply, their reply, and ask for clarification"). While
 * the team's last message waits for an answer, every page gets the
 * corner card (the feedback offer's look) with the message; its sheet
 * shows the whole exchange with a reply box. "Later" lets this page
 * through; the next page asks again, until the person answers.
 * `?feedback=thread` (the Telegram and push nudges' link) opens the
 * sheet straight away.
 *
 * In the interruption budget (lib/floating-slot) the card answers the
 * person's own message, so it needs no unused session; it still waits
 * for the cookie note, for a result or a drawer to close and for an
 * offer already on screen (a coach mark makes way for it), and it uses
 * up the session, so no offer follows it.
 */

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { usePathname, useSearchParams } from "next/navigation";
import { Loader2, X } from "lucide-react";
import BottomSheet from "@/components/BottomSheet";
import { useAuth } from "@/lib/auth-context";
import { useT } from "@/lib/locale-context";
import {
  THREAD_CHANGED_EVENT, THREAD_OPEN_EVENT, loadThread, refreshThreads, replyThread,
  type ThreadMessage, type ThreadState,
} from "@/lib/feedback-thread";
import { useInterruption } from "@/lib/floating-slot";
import { trackEvent } from "@/lib/tracking";
import { useIsMobileViewport } from "@/lib/use-install-prompt";
import { FLOAT_CARD, TAG } from "@/lib/ui-classes";

const SHOW_AFTER_MS = 1500;
const EXCERPT = 180;
// A white floating card, as the offer card (quiet UI, 2026-10-29): only
// the main button is indigo, the tag is grey.
const CARD = FLOAT_CARD;
const CTA = "rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-indigo-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2";
const LATER = "text-sm font-semibold text-gray-600 underline-offset-4 transition-colors hover:text-gray-900 hover:underline focus-visible:outline-none focus-visible:underline";

/** Which reply the card is about, on its events: the admin's ticket page
 *  shows next to each reply whether it was shown, dismissed or opened. */
function replyIds(thread: ThreadState | null): { ticket_id: number | null; message_id: number | null } {
  const last = thread ? [...thread.messages].reverse().find((m) => m.author === "team") : undefined;
  return { ticket_id: thread?.ticket?.id ?? null, message_id: last?.id ?? null };
}

export default function FeedbackThreadPrompt() {
  const t = useT();
  const pathname = usePathname();
  const search = useSearchParams();
  const { user } = useAuth();
  const [thread, setThread] = useState<ThreadState | null>(null);
  const [dismissedPath, setDismissedPath] = useState<string | null>(null);
  const [shown, setShown] = useState(false);
  const [open, setOpen] = useState(false);

  // The thread for this account; signed out there is none to ask for.
  useEffect(() => {
    if (!user) { setThread(null); return undefined; }
    let alive = true;
    const read = () => loadThread().then((s) => { if (alive) setThread(s); }).catch(() => {});
    read();
    window.addEventListener(THREAD_CHANGED_EVENT, read);
    return () => { alive = false; window.removeEventListener(THREAD_CHANGED_EVENT, read); };
  }, [user]);

  // The contact page's list asks for one conversation by id.
  useEffect(() => {
    if (!user) return undefined;
    const onOpen = (e: Event) => {
      const id = Number((e as CustomEvent<{ ticketId?: number }>).detail?.ticketId);
      if (!id) return;
      loadThread(true, id).then((s) => { if (s.ticket) { setThread(s); setOpen(true); } }).catch(() => {});
    };
    window.addEventListener(THREAD_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(THREAD_OPEN_EVENT, onOpen);
  }, [user]);

  // The nudge's link opens the sheet straight away.
  const wantsOpen = search.get("feedback") === "thread";
  const hasTicket = !!thread?.ticket;
  useEffect(() => { if (wantsOpen && hasTicket) setOpen(true); }, [wantsOpen, hasTicket]);

  // A question keeps the card up until answered; an informational reply
  // shows until the thread has been opened once.
  const asks = !!thread?.awaiting_user;
  // The contact page lists every conversation itself: no card over it.
  const onContact = /\/contact(\/|$)/.test(pathname || "");
  const due = (asks || !!thread?.unseen_reply) && dismissedPath !== pathname && !onContact;
  useEffect(() => {
    if (!due) { setShown(false); return undefined; }
    setShown(false);
    const id = window.setTimeout(() => setShown(true), SHOW_AFTER_MS);
    return () => window.clearTimeout(id);
  }, [due, pathname]);

  // While the card or its sheet is up, the other floating prompts wait;
  // the card itself waits for an offer already on screen.
  const turn = useInterruption("thread", ((shown && due) || open) && hasTicket);
  const cardUp = turn && shown && due && !open;

  useEffect(() => {
    if (cardUp) trackEvent("fbt.prompt", { path: pathname, ...replyIds(thread) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardUp]);

  if (!thread?.ticket) return null;
  const last = [...thread.messages].reverse().find((m) => m.author === "team");
  const excerpt = last ? (last.body.length > EXCERPT ? `${last.body.slice(0, EXCERPT).trimEnd()}…` : last.body) : "";
  const later = () => { trackEvent("fbt.prompt.later", { path: pathname, ...replyIds(thread) }); setDismissedPath(pathname); };
  const openSheet = () => { trackEvent("fbt.prompt.open", { asks, ...replyIds(thread) }); setOpen(true); };
  // Closing the sheet re-reads the thread (the sheet marked it as seen
  // when it opened): an opened informational reply no longer shows the
  // card, and the list and the menu's number follow.
  const closeSheet = () => {
    setOpen(false);
    refreshThreads();
    loadThread().then(setThread).catch(() => {});
  };
  const title = t(asks ? "fbt.title" : "fbt.title.info");

  return (
    <>
      {cardUp && (
        <section
          aria-label={title}
          className={`modal-enter fixed inset-x-4 bottom-[calc(4rem+env(safe-area-inset-bottom)+0.75rem)] z-[1096] rounded-2xl p-5 sm:inset-x-auto sm:bottom-6 sm:right-6 sm:w-[400px] ${CARD}`}
        >
          <span className={`inline-flex items-center rounded-lg px-2.5 py-1 text-xs font-bold ${TAG}`}>{t("fbt.pill")}</span>
          <p className="mt-2.5 text-xl font-bold leading-tight tracking-tight">{title}</p>
          <p className="mt-1.5 whitespace-pre-line text-sm leading-relaxed text-gray-600">{excerpt}</p>
          <div className="mt-4 flex items-center gap-4">
            <button type="button" onClick={openSheet} className={CTA}>{t(asks ? "fbt.cta" : "fbt.cta.read")}</button>
            <button type="button" onClick={later} className={LATER}>{t("fbo.later")}</button>
          </div>
        </section>
      )}
      {open && <ThreadSheet thread={thread} onClose={closeSheet} onChanged={setThread} />}
    </>
  );
}

function ThreadSheet({ thread, onClose, onChanged }: { thread: ThreadState; onClose: () => void; onChanged: (s: ThreadState) => void }) {
  const t = useT();
  const mobile = useIsMobileViewport();
  const [text, setText] = useState("");
  const [phase, setPhase] = useState<"form" | "sending" | "sent">("form");
  const [error, setError] = useState<string | null>(null);
  const [messages, setMessages] = useState<ThreadMessage[]>(thread.messages);
  const endRef = useRef<HTMLDivElement>(null);

  // Opening the thread marks the team's messages as read (the server
  // keeps that); the card stays until an answer is sent.
  const ticketId = thread.ticket?.id;
  useEffect(() => {
    let alive = true;
    loadThread(true, ticketId).then((s) => { if (alive) setMessages(s.messages); }).catch(() => {});
    return () => { alive = false; };
  }, [ticketId]);
  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [messages.length]);

  // Desktop: Escape closes and the page stops scrolling (the sheet does
  // both itself on phones).
  useEffect(() => {
    if (mobile) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [mobile, onClose]);

  const ready = text.trim().length >= 2;
  const send = async () => {
    if (!ready || !thread.ticket) return;
    setPhase("sending");
    setError(null);
    try {
      const next = await replyThread(thread.ticket.id, text.trim());
      trackEvent("fbt.sent", { chars: text.trim().length, ticket_id: thread.ticket.id });
      setMessages(next.messages);
      onChanged(next);
      setText("");
      setPhase("sent");
    } catch {
      setError(t("fbo.err.generic"));
      setPhase("form");
    }
  };

  // A conversation opened from the list is titled by its subject; the
  // feedback-for-Pro answers keep the generic title.
  const subject = thread.ticket && thread.ticket.category !== "pro_offer" ? thread.ticket.subject.trim() : "";
  const title = subject || t("fbt.sheet.title");
  const body = (
    <div className="space-y-3">
      {thread.ticket && (
        <div className="flex justify-end">
          <div className="max-w-[88%] whitespace-pre-line rounded-2xl bg-gray-100 px-3.5 py-2.5 text-sm leading-relaxed text-gray-900">
            <p className="mb-0.5 text-[11px] font-semibold text-gray-500">{t("fbt.you")}</p>
            {thread.ticket.message}
          </div>
        </div>
      )}
      {messages.map((m) => (
        <div key={m.id} className={`flex ${m.author === "user" ? "justify-end" : "justify-start"}`}>
          <div className={`max-w-[88%] whitespace-pre-line rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${m.author === "user" ? "bg-gray-100 text-gray-900" : "border border-gray-200 bg-white text-gray-900"}`}>
            <p className="mb-0.5 text-[11px] font-semibold text-gray-500">
              {m.author === "user" ? t("fbt.you") : "Mənzil.ai"}
            </p>
            {m.body}
          </div>
        </div>
      ))}
      {phase === "sent" && <p className="text-center text-xs font-medium text-emerald-600">{t("fbt.sent")}</p>}
      <div ref={endRef} />
    </div>
  );
  const footer = (
    <div className="space-y-2">
      {error && <p className="text-center text-sm text-rose-600" role="alert">{error}</p>}
      <textarea
        value={text}
        onChange={(e) => { setText(e.target.value); if (phase === "sent") setPhase("form"); }}
        rows={3}
        maxLength={1500}
        placeholder={t("fbt.placeholder")}
        aria-label={t("fbt.placeholder")}
        className="w-full resize-none rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm leading-6 text-gray-900 placeholder:text-gray-400 outline-none focus:ring-2 focus:ring-indigo-500"
      />
      <button
        type="button"
        onClick={send}
        disabled={!ready || phase === "sending"}
        className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 py-2.5 text-sm font-bold text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {phase === "sending" && <Loader2 className="h-4 w-4 animate-spin" />}
        {t("fbt.send")}
      </button>
    </div>
  );

  if (mobile) {
    return (
      <BottomSheet open onClose={onClose} title={title} footer={footer}>
        <div className="pb-2 pt-1">{body}</div>
      </BottomSheet>
    );
  }
  if (typeof document === "undefined") return null;
  return createPortal(
    <div role="dialog" aria-modal="true" aria-label={title} onClick={onClose}
      className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/40 p-4 modal-backdrop-enter">
      <div onClick={(e) => e.stopPropagation()}
        className="flex max-h-[90vh] w-full max-w-lg flex-col rounded-2xl border border-gray-200 bg-white shadow-[0_24px_64px_-24px_rgba(15,23,42,0.35)] modal-enter">
        <div className="flex items-start justify-between gap-3 px-6 pt-5">
          <p className="text-lg font-bold tracking-tight text-gray-900">{title}</p>
          <button type="button" onClick={onClose} aria-label={t("fbo.close")}
            className="-mr-2 rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="overflow-y-auto px-6 py-4">{body}</div>
        <div className="border-t border-gray-200 px-6 py-4">{footer}</div>
      </div>
    </div>,
    document.body,
  );
}
